import { useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { getPeopleMergePreview } from 'services/people'
import useMergeSelections from './useMergeSelections'
import useParseMergePreviewMeta from './useParseMergePreviewMeta'

function usePeopleMergePreview({ onPersonsChange } = {}) {
  const lastSearchedIds = useRef({ sourceId: null, targetId: null })

  const {
    data: mergePreviewResponse,
    mutate: getPreviewData,
    isLoading,
    isError,
    error,
    reset,
  } = useMutation(({ sourceId, targetId }) => getPeopleMergePreview(sourceId, targetId), {
    retry: false,
  })

  const selectedData = useMergeSelections(mergePreviewResponse?.data, { isError, reset })
  const { sourceId, targetId } = selectedData

  const mergePreviewMeta = useParseMergePreviewMeta(mergePreviewResponse?.meta)

  const handleSearch = () => {
    if (sourceId && targetId) {
      const idsChanged =
        lastSearchedIds.current.sourceId !== sourceId ||
        lastSearchedIds.current.targetId !== targetId

      if (idsChanged && onPersonsChange) {
        onPersonsChange()
      }

      lastSearchedIds.current = { sourceId, targetId }
      getPreviewData({ sourceId, targetId })
    }
  }

  const getFieldErrors = isError => {
    if (!isError) {
      return {
        sourceError: null,
        targetError: null,
      }
    }

    const errors = error?.response?.data?.errors || []
    const sourceError = errors.find(err => err.field === 'source_id')
    const targetError = errors.find(err => err.field === 'target_id')

    return {
      sourceError: sourceError?.message || null,
      targetError: targetError?.message || null,
    }
  }

  const { sourceError, targetError } = getFieldErrors(isError)

  return {
    // User selections & derived person/job data
    selectedData,
    // Parsed preview metadata from BE
    mergePreviewMeta,
    // Query state
    mergePreviewQuery: {
      isLoading,
      isError,
      sourceError,
      targetError,
    },
    handleSearch,
  }
}

export default usePeopleMergePreview
